import { PageHero } from "@/components/PageHero";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/motion";
import { EnquiryForm } from "@/components/EnquiryForm";
import { LeafMark } from "@/components/LeafMark";
import { CtaBand } from "@/components/CtaBand";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";
import { FileSearch, MessagesSquare, ShieldCheck } from "lucide-react";

export const SecondOpinion = () => {
    const { t, tHtml } = useLang();
    const STEPS = [
        {
            icon: FileSearch,
            title: t("second_opinion.step1.title", "Share your existing diagnosis"),
            desc: t("second_opinion.step1.desc", "Tell us your current diagnosis, treatment plan or the recommendation you want reviewed."),
        },
        {
            icon: MessagesSquare,
            title: t("second_opinion.step2.title", "We coordinate an independent review"),
            desc: t("second_opinion.step2.desc", "We coordinate an appropriate specialist consultation to review your case."),
        },
        {
            icon: ShieldCheck,
            title: t("second_opinion.step3.title", "Decide with confidence"),
            desc: t("second_opinion.step3.desc", "A clear understanding of your options — proceed, adjust or explore alternatives."),
        },
    ];
    const WHY = [
        {
            title: t("second_opinion.why1.title", "Confirm the diagnosis"),
            desc: t("second_opinion.why1.desc", "An independent specialist can confirm findings or flag things worth a closer look."),
        },
        {
            title: t("second_opinion.why2.title", "Explore other options"),
            desc: t("second_opinion.why2.desc", "Different approaches sometimes exist — a second opinion puts them on the table."),
        },
        {
            title: t("second_opinion.why3.title", "Peace of mind"),
            desc: t("second_opinion.why3.desc", "Whatever you decide, you decide it with a fuller picture and greater confidence."),
        },
    ];
    const PREPARE = [
        t("second_opinion.prepare1", "Your current diagnosis and how it was made"),
        t("second_opinion.prepare2", "The treatment plan or recommendation you have received"),
        t("second_opinion.prepare3", "Reports, scans and test results you already have"),
        t("second_opinion.prepare4", "What you would like a reviewing specialist to focus on"),
    ];
    const MINI_FAQ = [
        {
            q: t("second_opinion.faq1.q", "Does a second opinion replace my doctor?"),
            a: t("second_opinion.faq1.a", "No. It complements your relationship with your treating doctor — many patients share the second opinion with their own doctor."),
        },
        {
            q: t("second_opinion.faq2.q", "Can it be done before I travel?"),
            a: t("second_opinion.faq2.a", "Yes — reviews are usually coordinated remotely, based on your reports, before any travel is considered."),
        },
        {
            q: t("second_opinion.faq3.q", "How long does it take?"),
            a: t("second_opinion.faq3.a", "It depends on your case and the specialists involved. Once your reports are with us, we coordinate the review and keep you informed throughout."),
        },
    ];
    usePageMeta(
        t("meta.second_opinion.title", "Second Opinion"),
        t("meta.second_opinion.desc", "A clearer perspective for a brighter tomorrow — UniversHeal coordinates independent specialist second opinions in Kerala, India for international patients.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("second_opinion.eyebrow", "Second Opinion")}
                title={tHtml(
                    "second_opinion.title",
                    <>
                        A clearer perspective{" "}
                        <span className="italic text-pine">for a brighter tomorrow.</span>
                    </>
                )}
                intro={t("second_opinion.intro", "A diagnosis or treatment plan is one of the most important decisions you will ever make. We coordinate independent specialist reviews of your existing findings, so you can move forward with confidence.")}
            />

            <section className="pb-8">
                <div className="container-site">
                    <div className="grid sm:grid-cols-3 gap-5">
                        {WHY.map((item, i) => (
                            <Reveal key={item.title} delay={i * 0.07} className="h-full">
                                <div
                                    data-testid={`second-opinion-why-${i + 1}`}
                                    className="h-full bg-forest text-ivory rounded-2xl p-7"
                                >
                                    <p className="font-serif italic text-lg text-natural">
                                        {String(i + 1).padStart(2, "0")}
                                    </p>
                                    <h3 className="mt-4 font-serif font-medium text-2xl">{item.title}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-ivory/65">
                                        {item.desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <section className="pb-8">
                <div className="container-site">
                    <Reveal>
                        <div className="rounded-2xl overflow-hidden h-64 sm:h-[420px] shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                            <img
                                src={IMG.secondOpinion}
                                alt="A senior Indian specialist reviewing digital medical reports together with an international patient"
                                data-testid="second-opinion-image"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad pt-8">
                <div className="container-site">
                    <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-16 items-start">
                        <Reveal>
                            <div className="lg:sticky lg:top-28">
                                <h2 className="font-serif font-medium text-3xl sm:text-4xl text-forest leading-tight">
                                    {t("second_opinion.prepareTitle", "What to prepare")}
                                </h2>
                                <ul className="mt-7 space-y-4">
                                    {PREPARE.map((item) => (
                                        <li
                                            key={item}
                                            className="flex items-start gap-3 text-sm leading-relaxed text-charcoal/85"
                                        >
                                            <LeafMark className="w-4 h-4 mt-0.5 text-natural shrink-0" />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-8 text-xs leading-relaxed text-moss bg-sage/70 border border-pine/10 rounded-xl px-5 py-4">
                                    {t("second_opinion.prepareNote", "A second opinion is coordinated through independent specialists. It complements — never replaces — the relationship with your treating doctor.")}
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <EnquiryForm
                                service="Second Opinion"
                                submitLabel={t("form.requestOpinion", "Request a Second Opinion")}
                                testIdPrefix="second-opinion-form"
                            />
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="section-pad pt-0">
                <div className="container-site max-w-3xl">
                    <Reveal>
                        <h2 className="font-serif font-medium text-3xl sm:text-4xl text-forest">
                            {t("second_opinion.faqTitle", "Common questions")}
                        </h2>
                    </Reveal>
                    <div className="mt-8">
                        {MINI_FAQ.map((item, i) => (
                            <Reveal key={item.q} delay={i * 0.05}>
                                <div
                                    data-testid={`second-opinion-faq-${i + 1}`}
                                    className="py-6 border-t border-pine/15"
                                >
                                    <h3 className="font-serif font-medium text-xl text-forest">
                                        {item.q}
                                    </h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-moss">
                                        {item.a}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal>
                        <p className="mt-6 text-sm text-moss">
                            More questions? Visit the{" "}
                            <a href="/faq" data-testid="second-opinion-faq-link" className="font-semibold underline underline-offset-4 text-pine">
                                {t("second_opinion.faqLink", "international patient FAQ")}
                            </a>
                            .
                        </p>
                    </Reveal>
                </div>
            </section>

            <CtaBand
                headline={t("second_opinion.headline", "Still weighing a decision?")}
                text={t("second_opinion.text", "Share your reports — a clearer perspective may be one conversation away.")}
            />
        </>
    );
};
